import React, { useState } from 'react';
import { Box, Menu, MenuItem, Typography, useTheme } from '@mui/material';

const languages = [
  { code: 'en', label: 'English', icon: '/flags/gb.svg' },
  { code: 'ua', label: 'Українська', icon: '/flags/ua.svg' },
  { code: 'ru', label: 'русский', icon: '/flags/kz.svg' },
  { code: 'pl', label: 'Polski', icon: '/flags/pl.svg' },
];

const regions = [
  { code: 'ua', label: 'Ukraine', currency: 'UAH', icon: '/flags/ua.svg' },
  { code: 'eu', label: 'Europe', currency: 'EUR', icon: '/flags/eu.svg' },
  { code: 'asia', label: 'Central Asia', currency: 'USD', icon: '/flags/kz.svg' },
  { code: 'other', label: 'Other', currency: 'USD', icon: '/flags/globe.svg' },
];

export default function SettingsPanel({ region, setRegion, language, setLanguage, onRegionChangeRequest }) {
  const [langAnchor, setLangAnchor] = useState(null);
  const [regionAnchor, setRegionAnchor] = useState(null);
  const theme = useTheme();

  const currentRegion = regions.find(r => r.code === region);
  const currentLanguage = languages.find(l => l.code === language);

  const handleRegionClick = (event) => setRegionAnchor(event.currentTarget);
  const handleLanguageClick = (event) => setLangAnchor(event.currentTarget);

  const handleRegionClose = (newRegion) => {
    setRegionAnchor(null);
    if (newRegion && newRegion !== region) {
      // If there's a callback, use it to check if region change should proceed
      if (onRegionChangeRequest) {
        onRegionChangeRequest(region, newRegion);
      } else {
        setRegion(newRegion);
      }
    }
  };

  const handleLanguageClose = (newLanguage) => {
    setLangAnchor(null);
    if (newLanguage) {
      setLanguage(newLanguage);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {/* Region Selector */}
      <Box 
        onClick={handleRegionClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: theme.shape.borderRadius,
          px: 1.5,
          py: 0.7,
          cursor: 'pointer',
          bgcolor: 'background.default',
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <img 
          src={currentRegion?.icon || '/flags/globe.svg'} 
          alt={currentRegion?.label || 'Other'} 
          style={{ width: 20, height: 20, marginRight: 8, verticalAlign: 'middle', borderRadius: '3px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} 
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {currentRegion?.label || 'Other'}
        </Typography>
      </Box>

      {/* Language Selector */}
      <Box 
        onClick={handleLanguageClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: theme.shape.borderRadius,
          px: 1.5,
          py: 0.7,
          cursor: 'pointer',
          bgcolor: 'background.default',
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <img 
          src={currentLanguage?.icon || '/flags/gb.svg'} 
          alt={currentLanguage?.label || 'English'} 
          style={{ width: 20, height: 20, marginRight: 8, verticalAlign: 'middle', borderRadius: '3px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} 
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {currentLanguage?.label || 'English'}
        </Typography>
      </Box>

      {/* Region Menu */}
      <Menu 
        anchorEl={regionAnchor} 
        open={Boolean(regionAnchor)} 
        onClose={() => handleRegionClose(null)} 
        keepMounted
        PaperProps={{ 
          sx: { 
            borderRadius: 2, 
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 200,
            '& .MuiList-root': {
              py: 1
            }
          } 
        }}
      >
        {regions.map(r => (
          <MenuItem 
            key={r.code} 
            selected={r.code === region} 
            onClick={() => handleRegionClose(r.code)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              minWidth: 180, 
              py: 1.25,
              px: 2,
              '&:hover': {
                bgcolor: 'rgba(0, 200, 83, 0.08)'
              },
              '&.Mui-selected': {
                bgcolor: 'rgba(0, 200, 83, 0.12)',
                '&:hover': {
                  bgcolor: 'rgba(0, 200, 83, 0.16)'
                }
              }
            }}
          >
            <img 
              src={r.icon} 
              alt={r.label} 
              style={{ 
                width: 24, 
                height: 24, 
                objectFit: 'cover', 
                verticalAlign: 'middle', 
                borderRadius: '4px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)' 
              }} 
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {r.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {r.currency}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Language Menu */}
      <Menu 
        anchorEl={langAnchor} 
        open={Boolean(langAnchor)} 
        onClose={() => handleLanguageClose(null)} 
        keepMounted
        PaperProps={{ 
          sx: { 
            borderRadius: 2, 
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 180,
            '& .MuiList-root': {
              py: 1
            }
          } 
        }}
      >
        {languages.map(l => (
          <MenuItem 
            key={l.code} 
            selected={l.code === language} 
            onClick={() => handleLanguageClose(l.code)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              minWidth: 160, 
              py: 1.25,
              px: 2,
              '&:hover': {
                bgcolor: 'rgba(0, 200, 83, 0.08)'
              },
              '&.Mui-selected': {
                bgcolor: 'rgba(0, 200, 83, 0.12)',
                '&:hover': {
                  bgcolor: 'rgba(0, 200, 83, 0.16)'
                }
              }
            }}
          >
            <img 
              src={l.icon} 
              alt={l.label} 
              style={{ 
                width: 24, 
                height: 24, 
                verticalAlign: 'middle', 
                borderRadius: '4px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)' 
              }} 
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {l.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
